// src/App.tsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AdminAuthProvider, useAdminAuth } from './context/AdminAuthContext';
import Signup from './components/Signup';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import Profile from './components/Profile';
import Settings from './components/Settings';
import Jobs from './components/Jobs';
import SavedJobs from './components/SavedJobs';
import JobApplications from './components/JobApplications';
import ApplicationKanban from './components/ApplicationKanban';
import OfferComparison from './components/OfferComparison';
import ResumesManager from './components/ResumesManager';
import ResumeBuilder from './components/ResumeBuilder';
import CoverLetterBuilder from './components/CoverLetterBuilder';
import JobRecommendations from './components/JobRecommendations';
import EmailAccounts from './components/EmailAccounts';
import ResumeAnalysis from './components/ResumeAnalysis';
import InterviewPrep from './components/InterviewPrep';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import AdvancedJobSearch from './components/AdvancedJobSearch';
import NotificationSettings from './components/NotificationSettings';
import JobBoardSettings from './components/JobBoardSettings';
import SubscriptionSettings from './components/SubscriptionSettings';
import Community from './components/Community';
import CookieBanner from './components/CookieBanner';
import CommunityProfile from './components/CommunityProfile';
import MemberDirectory from './components/MemberDirectory';
import CommunityConnections from './components/CommunityConnections';
import AIJobMatches from './components/AIJobMatches';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import WorkflowBuilder from './components/WorkflowBuilder';
import StaffingFirms from './components/StaffingFirms';
import AIOptimization from './components/AIOptimization';
import CheckoutSuccess from './components/CheckoutSuccess';
import Landing from './components/Landing';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function ProtectedAdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdminAuthenticated } = useAdminAuth();
  return isAdminAuthenticated ? <>{children}</> : <Navigate to="/admin/login" replace />;
}

function App() {
  return (
    <AuthProvider>
      <AdminAuthProvider>
        <Router>
          <Routes>
            {/* Public landing — full-width, no wrapper. Section-based hybrid
                page; the hero hosts the career pipeline (WebGL where capable,
                CSS fallback otherwise — handled inside the hero). */}
            <Route path="/" element={<Landing />} />

            {/* Admin routes — full-width, no wrapper */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedAdminRoute>
                  <AdminDashboard />
                </ProtectedAdminRoute>
              }
            />

            {/* Public auth pages — full-width, no wrapper (full-screen split) */}
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />

            {/* All other routes with standard wrapper */}
            <Route path="*" element={
              <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
                <Routes>
            <Route
              path="/jobs"
              element={
                <ProtectedRoute>
                  <Jobs />
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/saved-jobs"
              element={
                <ProtectedRoute>
                  <SavedJobs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/applications"
              element={
                <ProtectedRoute>
                  <JobApplications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/applications/kanban"
              element={
                <ProtectedRoute>
                  <ApplicationKanban />
                </ProtectedRoute>
              }
            />
            <Route
              path="/offers/compare"
              element={
                <ProtectedRoute>
                  <OfferComparison />
                </ProtectedRoute>
              }
            />
            <Route
              path="/resumes"
              element={
                <ProtectedRoute>
                  <ResumesManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/resume-builder"
              element={
                <ProtectedRoute>
                  <ResumeBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/resume-builder/:resumeId"
              element={
                <ProtectedRoute>
                  <ResumeBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cover-letter-builder"
              element={
                <ProtectedRoute>
                  <CoverLetterBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cover-letter-builder/:letterId"
              element={
                <ProtectedRoute>
                  <CoverLetterBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/recommendations"
              element={
                <ProtectedRoute>
                  <JobRecommendations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-accounts"
              element={
                <ProtectedRoute>
                  <EmailAccounts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/resume-analysis"
              element={
                <ProtectedRoute>
                  <ResumeAnalysis />
                </ProtectedRoute>
              }
            />
            <Route
              path="/interview-prep"
              element={
                <ProtectedRoute>
                  <InterviewPrep />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <AnalyticsDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workflows"
              element={
                <ProtectedRoute>
                  <WorkflowBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/job-search"
              element={
                <ProtectedRoute>
                  <AdvancedJobSearch />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notification-settings"
              element={
                <ProtectedRoute>
                  <NotificationSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/job-board-settings"
              element={
                <ProtectedRoute>
                  <JobBoardSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription"
              element={
                <ProtectedRoute>
                  <SubscriptionSettings />
                </ProtectedRoute>
              }
            />
            {/* Stripe Checkout return URLs. These were never registered, so
                the "*" catch-all bounced paying users to /login and the
                /subscription/confirm activation fallback never ran. */}
            <Route
              path="/subscription/success"
              element={
                <ProtectedRoute>
                  <CheckoutSuccess />
                </ProtectedRoute>
              }
            />
            <Route path="/subscription/plans" element={<Navigate to="/subscription" replace />} />
            <Route
              path="/community"
              element={
                <ProtectedRoute>
                  <Community />
                </ProtectedRoute>
              }
            />
            <Route
              path="/community/profile"
              element={
                <ProtectedRoute>
                  <CommunityProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/community/members"
              element={
                <ProtectedRoute>
                  <MemberDirectory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/community/connections"
              element={
                <ProtectedRoute>
                  <CommunityConnections />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staffing-firms"
              element={
                <ProtectedRoute>
                  <StaffingFirms />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-optimization"
              element={
                <ProtectedRoute>
                  <AIOptimization />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-matches"
              element={
                <ProtectedRoute>
                  <AIJobMatches />
                </ProtectedRoute>
              }
            />

            {/* (Landing "/" is registered at the full-width level, outside this wrapper) */}

            {/* Catch-all unknown routes */}
            <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
              </div>
            } />
          </Routes>
        </Router>
        <CookieBanner />
      </AdminAuthProvider>
    </AuthProvider>
  );
}

export default App;